import { GlobalOutlined } from '@ant-design/icons';
import {
  getLocale,
  setLocale,
  SelectLang as UmiSelectLang,
  useModel,
} from '@umijs/max';
import React, { useEffect, useState } from 'react';
import { meApi, updateMyAccount } from '@/services/auth';
import { getToken } from '@/utils/auth';
import {
  DEFAULT_LOCALE,
  normalizeAppLocale,
  SUPPORTED_LOCALES,
  toLangKeyFromLocale,
} from '@/utils/locale';

export type SiderTheme = 'light' | 'dark';

type LocaleOption = {
  lang: string;
  label?: string;
  icon?: string;
  title?: string;
};

const SUPPORTED_LOCALE_SET = new Set<string>(SUPPORTED_LOCALES);

const filterSupportedLocales = (locales: LocaleOption[]) => {
  return locales
    .filter((locale) => SUPPORTED_LOCALE_SET.has(locale.lang))
    .map((locale) => {
      if (locale.lang === 'vi-VN') {
        return {
          ...locale,
          label: 'Tiếng Việt',
          title: 'Tiếng Việt',
        };
      }
      return {
        ...locale,
        label: 'English',
        title: 'English',
      };
    });
};

export const SelectLang: React.FC = () => {
  const [localeReady, setLocaleReady] = useState(false);
  const [selectedLocale, setSelectedLocale] = useState(() =>
    normalizeAppLocale(getLocale?.()),
  );
  const { setInitialState } = useModel('@@initialState');

  useEffect(() => {
    const currentLocale = getLocale?.();
    const normalizedLocale = normalizeAppLocale(currentLocale);
    if (normalizedLocale !== currentLocale) {
      setLocale(DEFAULT_LOCALE, false);
    }
    setSelectedLocale(normalizedLocale);
    setLocaleReady(true);
  }, []);

  useEffect(() => {
    const onLanguageChange = () => {
      setSelectedLocale(normalizeAppLocale(getLocale?.()));
    };
    window.addEventListener('languagechange', onLanguageChange);
    return () => {
      window.removeEventListener('languagechange', onLanguageChange);
    };
  }, []);

  if (!localeReady) {
    return null;
  }

  return (
    <UmiSelectLang
      key={selectedLocale}
      icon={<GlobalOutlined />}
      postLocalesData={filterSupportedLocales}
      reload={false}
      onItemClick={({ key }) => {
        const nextLocale = normalizeAppLocale(String(key));
        setLocale(nextLocale, false);
        setSelectedLocale(nextLocale);
        if (!getToken()) {
          return;
        }
        void (async () => {
          try {
            const currentUser = await meApi();
            const refreshedUser = await updateMyAccount({
              firstName: currentUser.firstName,
              lastName: currentUser.lastName,
              email: currentUser.email,
              imageUrl: currentUser.imageUrl,
              langKey: toLangKeyFromLocale(nextLocale),
            });
            setInitialState((prev: any) => ({
              ...prev,
              currentUser: refreshedUser,
            }));
          } catch {
            // ignore sync failure to avoid blocking locale switching
          }
        })();
      }}
      style={{
        padding: 4,
      }}
    />
  );
};
